import { CheckoutBlockDto } from "@intelligems/ig-types/dtos/checkout-blocks";
import {
  UpsellCompareAtPriceDisplay,
  UpsellProductRulesActionQuery,
  UpsellProductRulesDto,
  UpsellProductRulesNodeType,
} from "@intelligems/ig-types/dtos/upsell-product-rules";
import { VariationDto } from "@intelligems/ig-types/dtos/variations";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction, type Reducer } from "@reduxjs/toolkit";
import { ProductPickerProduct } from "@src/store/slices/productPicker/types";
import { selectionToUpsellProductItems } from "@src/store/slices/productPicker/util/upsellSelection";
import {
  initialIUpsellProductRulesState,
  IUpsellProductRulesState,
} from "@src/store/slices/upsellProductRules/types";

interface BaseProduct {
  id: string;
  title: string;
  image?: string;
  selectedVariantIds?: string[];
}

const upsellProductRulesSlice = rtk.createSlice({
  name: "upsellProductRules",
  initialState: initialIUpsellProductRulesState,
  reducers: {
    resetUpsellProductRules: () => {
      return initialIUpsellProductRulesState;
    },
    initUpsellProductRulesFromVariations: (
      draft,
      action: PayloadAction<{
        variations: VariationDto[];
      }>
    ) => {
      const { variations } = action.payload;
      const allRules: UpsellProductRulesDto[] = [];

      variations.forEach((variation) => {
        variation.checkoutBlocks?.forEach((block: CheckoutBlockDto) => {
          if (
            (block.blockType === "upsell" ||
              block.blockType === "postPurchaseUpsell") &&
            block.upsellProductRules
          ) {
            allRules.push(...block.upsellProductRules);
          }
        });
      });

      if (allRules.length > 0) {
        draft.isSuccess = true;
        draft.upsellProductRules = allRules;
        draft.storedUpsellProductRules = allRules;
      }
    },
    addUpsellProductRule: (
      draft,
      action: PayloadAction<{
        rule: UpsellProductRulesDto;
      }>
    ) => {
      draft.upsellProductRules.push(action.payload.rule);
    },
    setUpsellProductRulesByVariationId: (
      draft,
      action: PayloadAction<{
        variationId: string;
        rules: UpsellProductRulesDto[];
      }>
    ) => {
      const { variationId, rules } = action.payload;

      draft.upsellProductRules = draft.upsellProductRules.filter(
        (rule) => rule.variationId !== variationId
      );

      if (rules.length > 0) {
        draft.isSuccess = true;
        draft.upsellProductRules.push(...rules);
      }
    },
    removeUpsellProductRule: (
      draft,
      action: PayloadAction<{
        ruleId: string;
      }>
    ) => {
      const { ruleId } = action.payload;
      const index = draft.upsellProductRules.findIndex((r) => r.id === ruleId);
      if (index !== -1) {
        draft.upsellProductRules.splice(index, 1);
      }
    },
    reorderUpsellProductRules: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        startIndex: number;
        endIndex: number;
      }>
    ) => {
      const { checkoutBlockId, startIndex, endIndex } = action.payload;

      const blockRules = draft.upsellProductRules
        .map((rule, index) => ({ rule, originalIndex: index }))
        .filter((item) => item.rule.checkoutBlockId === checkoutBlockId)
        .sort((a, b) => a.rule.priority - b.rule.priority);

      const [movedRule] = blockRules.splice(startIndex, 1);
      blockRules.splice(endIndex, 0, movedRule);

      blockRules.forEach((item, newIndex) => {
        draft.upsellProductRules[item.originalIndex].priority = newIndex;
      });
    },
    updateUpsellProductRuleAction: (
      draft,
      action: PayloadAction<{
        ruleId: string;
        actionMethod: UpsellProductRulesDto["action"];
      }>
    ) => {
      const { ruleId, actionMethod } = action.payload;

      const ruleIndex = draft.upsellProductRules.findIndex(
        (r) => r.id === ruleId
      );

      if (ruleIndex !== -1) {
        draft.upsellProductRules[ruleIndex].action = actionMethod;
      }
    },
    updateUpsellProductRuleActionProductIds: (
      draft,
      action: PayloadAction<{
        ruleId: string;
        selectedProducts: BaseProduct[];
        allProducts: ProductPickerProduct[];
        expressionsIndex: number;
      }>
    ) => {
      const { ruleId, selectedProducts, allProducts, expressionsIndex } =
        action.payload;

      const ruleIndex = draft.upsellProductRules.findIndex(
        (r) => r.id === ruleId
      );

      if (ruleIndex === -1) {
        return;
      }

      const items = selectionToUpsellProductItems(
        selectedProducts,
        allProducts
      );
      const displayData: Record<string, { title?: string; image?: string }> =
        {};
      for (const item of items) {
        displayData[item.productId] = {
          title: item.productTitle,
          image: item.productImage ?? undefined,
        };
      }

      const currentExpression =
        draft.upsellProductRules[ruleIndex].action.expression;
      const queries = currentExpression
        .filter((node) => node.query)
        .map((node) => node.query!);

      if (queries[expressionsIndex]) {
        queries[expressionsIndex] = {
          ...queries[expressionsIndex],
          type: "productId",
          key: "productId",
          value: items,
          displayData,
        } as UpsellProductRulesActionQuery;

        const newExpression: UpsellProductRulesNodeType[] = [];
        for (let i = 0; i < queries.length; i++) {
          if (i > 0) {
            newExpression.push({ operator: "and" });
          }
          newExpression.push({ query: queries[i] });
        }
        draft.upsellProductRules[ruleIndex].action.expression = newExpression;
      }
    },
    updateAllRulesDiscount: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        discountType: "none" | "percentage" | "fixed";
        discountValue: number | null;
        compareAtPriceDisplay: UpsellCompareAtPriceDisplay;
      }>
    ) => {
      const {
        checkoutBlockId,
        discountType,
        discountValue,
        compareAtPriceDisplay,
      } = action.payload;

      draft.upsellProductRules.forEach((rule) => {
        if (rule.checkoutBlockId === checkoutBlockId) {
          rule.action.discountType = discountType;
          rule.action.discountValue = discountValue;
          rule.action.compareAtPriceDisplay = compareAtPriceDisplay;
        }
      });
    },
    processStoredUpsellProductRules: (draft) => {
      draft.storedUpsellProductRules = draft.upsellProductRules;
    },
  },
  selectors: {
    getUpsellProductRules: (state) => state.upsellProductRules,
  },
});

export const {
  initUpsellProductRulesFromVariations,
  addUpsellProductRule,
  setUpsellProductRulesByVariationId,
  removeUpsellProductRule,
  reorderUpsellProductRules,
  updateUpsellProductRuleAction,
  updateUpsellProductRuleActionProductIds,
  updateAllRulesDiscount,
  processStoredUpsellProductRules,
  resetUpsellProductRules,
} = upsellProductRulesSlice.actions;

export const upsellProductRulesReducer = upsellProductRulesSlice.reducer;

export const getUpsellProductRules = (state: {
  upsellProductRules: IUpsellProductRulesState;
}) => state.upsellProductRules.upsellProductRules;
